"use client";

import { ArrowUpRight, BookOpen, Check, Code2, Compass, Copy, Link as LinkIcon, PenLine, Settings2 } from "lucide-react";
import Prism from "prismjs";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Descendant } from "slate";
import { Editor, createEditor } from "slate";
import { Editable, ReactEditor, Slate, useSlateStatic, withReact } from "slate-react";
import "@/app/code-theme.css";

// ─── Global Prism Initialization ─────────────────────────────────────────────
if (typeof window !== "undefined") {
  (window as any).Prism = Prism;
} else if (typeof global !== "undefined") {
  (global as any).Prism = Prism;
}

try {
  require("prismjs/components/prism-markup");
  require("prismjs/components/prism-markup-templating");
  require("prismjs/components/prism-css");
  require("prismjs/components/prism-clike");
  require("prismjs/components/prism-javascript");
  require("prismjs/components/prism-typescript");
  require("prismjs/components/prism-jsx");
  require("prismjs/components/prism-tsx");
  require("prismjs/components/prism-python");
  require("prismjs/components/prism-java");
  require("prismjs/components/prism-c");
  require("prismjs/components/prism-cpp");
  require("prismjs/components/prism-csharp");
  require("prismjs/components/prism-go");
  require("prismjs/components/prism-rust");
  require("prismjs/components/prism-sql");
  require("prismjs/components/prism-bash");
  require("prismjs/components/prism-php");
  require("prismjs/components/prism-ruby");
  require("prismjs/components/prism-yaml");
  require("prismjs/components/prism-markdown");
} catch (_) {}

// ─── Context & Style Maps ───────────────────────────────────────────────────
export const ListContext = React.createContext<"bullet" | "number" | null>(null);

// Docs-only features (doc cards, code tabs) are disabled for regular articles.
const DocsFeaturesContext = React.createContext<boolean>(true);

const TEXT_COLOR_MAP: Record<string, string> = {
  gray: "markdown-text-gray",
  brown: "markdown-text-brown",
  orange: "markdown-text-orange",
  yellow: "markdown-text-yellow",
  green: "markdown-text-green",
  blue: "markdown-text-blue",
  purple: "markdown-text-purple",
  pink: "markdown-text-pink",
  red: "markdown-text-red",
};

const HIGHLIGHT_MAP: Record<string, string> = {
  gray: "markdown-highlight-gray px-1 py-0.5 rounded-sm",
  brown: "markdown-highlight-brown px-1 py-0.5 rounded-sm",
  orange: "markdown-highlight-orange px-1 py-0.5 rounded-sm",
  yellow: "markdown-highlight-yellow px-1 py-0.5 rounded-sm",
  green: "markdown-highlight-green px-1 py-0.5 rounded-sm",
  blue: "markdown-highlight-blue px-1 py-0.5 rounded-sm",
  purple: "markdown-highlight-purple px-1 py-0.5 rounded-sm",
  pink: "markdown-highlight-pink px-1 py-0.5 rounded-sm",
  red: "markdown-highlight-red px-1 py-0.5 rounded-sm",
};

const CELL_TEXT_COLOR_MAP: Record<string, string> = {
  gray: "markdown-cell-text-gray",
  brown: "markdown-cell-text-brown",
  orange: "markdown-cell-text-orange",
  yellow: "markdown-cell-text-yellow",
  green: "markdown-cell-text-green",
  blue: "markdown-cell-text-blue",
  purple: "markdown-cell-text-purple",
  pink: "markdown-cell-text-pink",
  red: "markdown-cell-text-red",
};

const CELL_BG_COLOR_MAP: Record<string, string> = {
  gray: "markdown-cell-bg-gray",
  brown: "markdown-cell-bg-brown",
  orange: "markdown-cell-bg-orange",
  yellow: "markdown-cell-bg-yellow",
  green: "markdown-cell-bg-green",
  blue: "markdown-cell-bg-blue",
  purple: "markdown-cell-bg-purple",
  pink: "markdown-cell-bg-pink",
  red: "markdown-cell-bg-red",
};

// ─── Security Helpers ───────────────────────────────────────────────────────
export function sanitizeUrl(url?: string | null): string {
  if (!url) return "";
  const trimmed = url.trim();

  if (trimmed === "" || trimmed === "#") {
    return trimmed;
  }

  if (trimmed.startsWith("/") || trimmed.startsWith("#")) {
    return trimmed;
  }

  const DANGEROUS_PROTOCOLS = /^(javascript|data|vbscript|file|blob):/i;
  if (DANGEROUS_PROTOCOLS.test(trimmed)) {
    return "#";
  }

  if (/^(https?:\/\/|mailto:|tel:)/i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
}

export function escapeHtml(text: string): string {
  if (!text) return "";
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}

export function unescapeHtml(text: string): string {
  if (!text) return "";
  return text
    .replace(/&amp;gt;/g, ">")
    .replace(/&amp;lt;/g, "<")
    .replace(/&amp;amp;/g, "&")
    .replace(/&gt;/g, ">")
    .replace(/&lt;/g, "<")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&");
}

function sanitizeHtmlForCode(html: string): string {
  if (!html) return "";
  return html.replace(/<(?!\/?(span|code)\b)[^>]*>/gi, "");
}

// ─── Markdown Parser Implementation ─────────────────────────────────────────
// NOTE: Mirror of `markdown/parser.ts` (kept inline so this file stays standalone and can be copied
// to other projects). Keep the two in sync. The only intentional difference: no trailing empty
// paragraph is appended (that exists purely so editor users can click below the content).
function applyMarkToInlines(nodes: any[], mark: string, value: any = true): any[] {
  return nodes.map((n) => {
    if (n.text !== undefined) {
      return { ...n, [mark]: value };
    }
    if (n.children) {
      return {
        ...n,
        children: applyMarkToInlines(n.children, mark, value),
      };
    }
    return n;
  });
}

function parseInlines(text: string): any[] {
  if (!text) return [{ text: "" }];

  const leaves: any[] = [];
  let remaining = text;

  while (remaining) {
    // Check for Link wrapping an Image: [![alt](img_url)](link_url) or with attributes {...}
    const linkedImgMatch = remaining.match(/^\[(!\[[^\]]*\]\([^)]+\))\]\(((?:\([^()\s]*\)|[^)\s])+)(?:\s+["'].*?["'])?\)(?:\{([^}]*)\})?/);
    if (linkedImgMatch) {
      const imgText = linkedImgMatch[1];
      const linkUrl = linkedImgMatch[2];
      const attrStr = linkedImgMatch[3] || "";
      const attrs: Record<string, string> = {};
      if (attrStr) {
        attrStr.split(/[\s,]+/).forEach((pair) => {
          const [k, v] = pair.split("=");
          if (k && v) attrs[k.trim()] = v.trim();
        });
      }
      const inlines = parseInlines(imgText);
      if (inlines.length === 1 && inlines[0].type === "inline-image") {
        leaves.push({
          ...inlines[0],
          linkUrl: sanitizeUrl(linkUrl),
          naturalWidth: attrs.nw ? Number.parseInt(attrs.nw, 10) : inlines[0].naturalWidth,
          naturalHeight: attrs.nh ? Number.parseInt(attrs.nh, 10) : inlines[0].naturalHeight,
          width: attrs.width ? Number.parseInt(attrs.width, 10) : inlines[0].width,
        });
      } else {
        leaves.push({
          type: "link",
          url: sanitizeUrl(linkUrl),
          children: inlines,
        });
      }
      remaining = remaining.substring(linkedImgMatch[0].length);
      continue;
    }

    // Check for Link: [text](url)
    // Supports nested inline images within the link text
    const linkMatch = remaining.match(/^\[((?:[^\]]|!\[[^\]]*\]\([^)]+\))*)\]\(((?:\([^()\s]*\)|[^)\s])+)(?:\s+["'].*?["'])?\)/);
    if (linkMatch) {
      const linkText = linkMatch[1];
      const linkUrl = linkMatch[2];
      leaves.push({
        type: "link",
        url: sanitizeUrl(linkUrl),
        children: parseInlines(linkText),
      });
      remaining = remaining.substring(linkMatch[0].length);
      continue;
    }

    // Check for inline HTML Link tag: <a href="...">text</a> (supports multi-line attributes/text)
    const htmlLinkMatch = remaining.match(/^<a\s+([\s\S]*?)>([\s\S]*?)<\/a>/i);
    if (htmlLinkMatch) {
      const attrStr = htmlLinkMatch[1];
      const linkText = htmlLinkMatch[2];
      const hrefMatch = attrStr.match(/href=["']([\s\S]*?)["']/i);
      const linkUrl = hrefMatch ? hrefMatch[1] : "";
      leaves.push({
        type: "link",
        url: sanitizeUrl(linkUrl),
        children: parseInlines(linkText),
      });
      remaining = remaining.substring(htmlLinkMatch[0].length);
      continue;
    }

    // Check for inline Image: ![alt](url) or with attributes {...}
    const imgMatch = remaining.match(/^!\[([^\]]*)\]\(((?:\([^()\s]*\)|[^)\s])+)(?:\s+["'].*?["'])?\)(?:\{([^}]*)\})?/);
    if (imgMatch) {
      const altText = imgMatch[1];
      const url = imgMatch[2];
      const attrStr = imgMatch[3] || "";
      const attrs: Record<string, string> = {};
      if (attrStr) {
        attrStr.split(/[\s,]+/).forEach((pair) => {
          const [k, v] = pair.split("=");
          if (k && v) attrs[k.trim()] = v.trim();
        });
      }
      leaves.push({
        type: "inline-image",
        url: sanitizeUrl(url),
        alt: altText,
        naturalWidth: attrs.nw ? Number.parseInt(attrs.nw, 10) : undefined,
        naturalHeight: attrs.nh ? Number.parseInt(attrs.nh, 10) : undefined,
        width: attrs.width ? Number.parseInt(attrs.width, 10) : undefined,
        children: [{ text: "" }],
      });
      remaining = remaining.substring(imgMatch[0].length);
      continue;
    }

    // Check for inline HTML Linked Image: <a href="..."><img src="..." data-inline="true" /></a>
    const htmlLinkedImgMatch = remaining.match(/^<a\s+([^>]*?)>\s*<img\s+([^>]*?)\/?>\s*<\/a>/i);
    if (htmlLinkedImgMatch) {
      const aAttrStr = htmlLinkedImgMatch[1];
      const imgAttrStr = htmlLinkedImgMatch[2];

      const hrefMatch = aAttrStr.match(/href=["']([\s\S]*?)["']/i);
      const srcMatch = imgAttrStr.match(/src=["']([\s\S]*?)["']/i);
      const altMatch = imgAttrStr.match(/alt=["']([\s\S]*?)["']/i);
      const widthMatch = imgAttrStr.match(/width=["']([\s\S]*?)["']/i);
      const heightMatch = imgAttrStr.match(/height=["']([\s\S]*?)["']/i);
      const nwMatch = imgAttrStr.match(/data-nw=["']([\s\S]*?)["']/i);
      const nhMatch = imgAttrStr.match(/data-nh=["']([\s\S]*?)["']/i);

      if (srcMatch) {
        const url = srcMatch[1];
        const altText = altMatch ? altMatch[1] : "";
        const width = widthMatch ? Number(widthMatch[1]) : undefined;
        const height = heightMatch ? Number(heightMatch[1]) : undefined;
        const naturalWidth = nwMatch ? Number(nwMatch[1]) : undefined;
        const naturalHeight = nhMatch ? Number(nhMatch[1]) : undefined;
        const linkUrl = hrefMatch ? hrefMatch[1] : "";

        leaves.push({
          type: "inline-image",
          url: sanitizeUrl(url),
          linkUrl: sanitizeUrl(linkUrl),
          alt: altText,
          width,
          height,
          naturalWidth,
          naturalHeight,
          children: [{ text: "" }],
        });
        remaining = remaining.substring(htmlLinkedImgMatch[0].length);
        continue;
      }
    }

    // Check for inline HTML Image tag: <img src="..." alt="..." /> (supports multi-line attributes)
    const htmlImgMatch = remaining.match(/^<img\s+([^>]*?)\/?>/i);
    if (htmlImgMatch) {
      const attrStr = htmlImgMatch[1];
      const srcMatch = attrStr.match(/src=["']([\s\S]*?)["']/i);
      const altMatch = attrStr.match(/alt=["']([\s\S]*?)["']/i);
      const widthMatch = attrStr.match(/width=["']([\s\S]*?)["']/i);
      const heightMatch = attrStr.match(/height=["']([\s\S]*?)["']/i);
      // data-nw/data-nh: natural pixel dims for skeleton placeholder
      const nwMatch = attrStr.match(/data-nw=["']([\s\S]*?)["']/i);
      const nhMatch = attrStr.match(/data-nh=["']([\s\S]*?)["']/i);

      const url = srcMatch ? srcMatch[1] : "";
      const altText = altMatch ? altMatch[1] : "";
      const width = widthMatch ? Number(widthMatch[1]) : undefined;
      const height = heightMatch ? Number(heightMatch[1]) : undefined;
      const naturalWidth = nwMatch ? Number(nwMatch[1]) : undefined;
      const naturalHeight = nhMatch ? Number(nhMatch[1]) : undefined;

      leaves.push({
        type: "inline-image",
        url: sanitizeUrl(url),
        alt: altText,
        width,
        height,
        naturalWidth,
        naturalHeight,
        children: [{ text: "" }],
      });
      remaining = remaining.substring(htmlImgMatch[0].length);
      continue;
    }

    // Check for HTML br tag
    const brMatch = remaining.match(/^<br\s*\/?>/i);
    if (brMatch) {
      leaves.push({ text: " " });
      remaining = remaining.substring(brMatch[0].length);
      continue;
    }

    // Check for inline HTML Bold tag: <b>text</b> or strong>text</strong>
    const htmlBoldMatch = remaining.match(/^<(b|strong)>([\s\S]*?)<\/\1>/i);
    if (htmlBoldMatch) {
      const boldInlines = parseInlines(htmlBoldMatch[2].replace(/\n/g, " "));
      leaves.push(...applyMarkToInlines(boldInlines, "bold"));
      remaining = remaining.substring(htmlBoldMatch[0].length);
      continue;
    }

    // Check for inline HTML Italic tag: <i>text</i> or <em>text</em>
    const htmlItalicMatch = remaining.match(/^<(i|em)>([\s\S]*?)<\/\1>/i);
    if (htmlItalicMatch) {
      const italicInlines = parseInlines(htmlItalicMatch[2].replace(/\n/g, " "));
      leaves.push(...applyMarkToInlines(italicInlines, "italic"));
      remaining = remaining.substring(htmlItalicMatch[0].length);
      continue;
    }

    // Empty emphasis markers (e.g. "****" produced by an empty bold leaf in old data): drop them
    const emptyMarkMatch = remaining.match(/^(?:\*\*\*\*|____)(?![*_])/);
    if (emptyMarkMatch) {
      remaining = remaining.substring(emptyMarkMatch[0].length);
      continue;
    }

    // Check for Bold Italic: ***text*** or ___text___
    const boldItalicMatch = remaining.match(/^(\*\*\*|___)([\s\S]+?)\1/);
    if (boldItalicMatch) {
      const innerInlines = parseInlines(boldItalicMatch[2]);
      const withBold = applyMarkToInlines(innerInlines, "bold");
      leaves.push(...applyMarkToInlines(withBold, "italic"));
      remaining = remaining.substring(boldItalicMatch[0].length);
      continue;
    }

    // Check for Bold: **text** or __text__
    const boldMatch = remaining.match(/^(\*\*|__)([\s\S]+?)\1/);
    if (boldMatch) {
      const innerInlines = parseInlines(boldMatch[2]);
      leaves.push(...applyMarkToInlines(innerInlines, "bold"));
      remaining = remaining.substring(boldMatch[0].length);
      continue;
    }

    // Check for Italic: *text* or _text_
    const italicMatch = remaining.match(/^(\*|_)([\s\S]+?)\1/);
    if (italicMatch) {
      const innerInlines = parseInlines(italicMatch[2]);
      leaves.push(...applyMarkToInlines(innerInlines, "italic"));
      remaining = remaining.substring(italicMatch[0].length);
      continue;
    }

    // Check for Underline: <u>text</u>
    const underlineMatch = remaining.match(/^<u>(.*?)<\/u>/);
    if (underlineMatch) {
      const underlineInlines = parseInlines(underlineMatch[1]);
      leaves.push(...applyMarkToInlines(underlineInlines, "underline"));
      remaining = remaining.substring(underlineMatch[0].length);
      continue;
    }

    // Check for HTML Strikethrough: <s>text</s>, <del>text</del>, or <strike>text</strike>
    const htmlStrikeMatch = remaining.match(/^<(s|del|strike)>([\s\S]*?)<\/\1>/i);
    if (htmlStrikeMatch) {
      const strikethroughInlines = parseInlines(htmlStrikeMatch[2]);
      leaves.push(...applyMarkToInlines(strikethroughInlines, "strikethrough"));
      remaining = remaining.substring(htmlStrikeMatch[0].length);
      continue;
    }

    // Check for Strikethrough: ~~text~~
    const strikethroughMatch = remaining.match(/^~~(.*?)~~/);
    if (strikethroughMatch) {
      const strikethroughInlines = parseInlines(strikethroughMatch[1]);
      leaves.push(...applyMarkToInlines(strikethroughInlines, "strikethrough"));
      remaining = remaining.substring(strikethroughMatch[0].length);
      continue;
    }

    // Check for inline HTML span tag: <span data-text="..." data-bg="...">text</span>
    const htmlSpanMatch = remaining.match(/^<span\s+([^>]*?)>([\s\S]*?)<\/span>/i);
    if (htmlSpanMatch) {
      const attrStr = htmlSpanMatch[1];
      const spanText = htmlSpanMatch[2];
      const textMatch = attrStr.match(/data-text=["']([^"']+)["']/i);
      const bgMatch = attrStr.match(/data-bg=["']([^"']+)["']/i);

      let spanInlines = parseInlines(spanText);
      if (textMatch) {
        spanInlines = applyMarkToInlines(spanInlines, "textColor", textMatch[1]);
      }
      if (bgMatch) {
        spanInlines = applyMarkToInlines(spanInlines, "highlight", bgMatch[1]);
      }
      leaves.push(...spanInlines);
      remaining = remaining.substring(htmlSpanMatch[0].length);
      continue;
    }

    // Check for Markdown Highlight: ==text==
    const highlightMatch = remaining.match(/^==(.*?)==/);
    if (highlightMatch) {
      const highlightInlines = parseInlines(highlightMatch[1]);
      leaves.push(...applyMarkToInlines(highlightInlines, "highlight", "yellow"));
      remaining = remaining.substring(highlightMatch[0].length);
      continue;
    }

    // Check for Code: `text`
    const codeMatch = remaining.match(/^(`+)([\s\S]*?)(?<!`)\1(?!`)/);
    if (codeMatch) {
      let content = codeMatch[2];
      if (content.startsWith(" ") && content.endsWith(" ") && content.trim() !== "") {
        content = content.slice(1, -1);
      }
      leaves.push({ text: content, code: true });
      remaining = remaining.substring(codeMatch[0].length);
      continue;
    }

    // Grab normal text until the next markdown token
    const nextTokenMatch = remaining.match(/^[^\*_`\[<~=]+/);
    if (nextTokenMatch) {
      leaves.push({ text: unescapeHtml(nextTokenMatch[0]) });
      remaining = remaining.substring(nextTokenMatch[0].length);
    } else {
      leaves.push({ text: unescapeHtml(remaining[0]) });
      remaining = remaining.substring(1);
    }
  }

  return leaves.length > 0 ? leaves : [{ text: "" }];
}

/**
 * A GFM separator row such as `| --- | :-: |`. It must be the second line of the table, or consist only of cells
 * with at least three dashes. A data row of placeholders like `| - | - |` is real content and must be kept.
 */
function isTableSeparatorRow(row: string, lineIndex: number): boolean {
  if (!/^\|?[\s:|-]+\|?$/.test(row) || !row.includes("-")) return false;
  const cells = splitTableRow(row);
  if (cells.length === 0 || !cells.every((c) => /^:?-+:?$/.test(c))) return false;
  return lineIndex === 1 || cells.every((c) => /^:?-{3,}:?$/.test(c));
}

/**
 * The cell-level color wrapper written by the serializer: a `<span data-bg data-text>` whose matching closing tag
 * is the very end of the cell. A span that only covers part of the text (a colored leaf) is NOT a wrapper.
 */
function matchWrappingSpan(text: string): { attrs: string; inner: string } | null {
  const open = text.match(/^<span\s+([^>]*?)>/i);
  if (!open) return null;
  const tagRe = /<(\/?)span\b[^>]*>/gi;
  tagRe.lastIndex = open[0].length;
  let depth = 1;
  for (let m = tagRe.exec(text); m; m = tagRe.exec(text)) {
    depth += m[1] ? -1 : 1;
    if (depth === 0) {
      return m.index + m[0].length === text.length ? { attrs: open[1], inner: text.slice(open[0].length, m.index) } : null;
    }
  }
  return null;
}

/** Splits a GFM row into trimmed cell strings. `\|` is an escaped pipe that stays inside the cell. */
function splitTableRow(row: string): string[] {
  let s = row.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  const cells: string[] = [];
  let cur = "";
  for (let c = 0; c < s.length; c++) {
    const ch = s[c];
    if (ch === "\\" && s[c + 1] === "|") {
      cur += "|";
      c++;
      continue;
    }
    if (ch === "|") {
      cells.push(cur.trim());
      cur = "";
      continue;
    }
    cur += ch;
  }
  cells.push(cur.trim());
  return cells;
}

function isBlockStart(l: string): boolean {
  const t = l.trim();
  if (!t) return true;
  if (t.startsWith("#")) {
    return !!t.match(/^(#{1,6})\s+(.*)$/);
  }
  if (t.startsWith(">") || t.startsWith("&gt;") || t.startsWith("&amp;gt;")) return true;
  if (t.startsWith("- ") || t === "-" || t.startsWith("* ") || t === "*" || t.startsWith("+ ") || t === "+") return true;
  if (/^\d+[.\)](\s|$)/.test(t)) return true;
  if (t.startsWith("|")) return true;
  if (t.startsWith("```") || /^~{3,}[^~]*$/.test(t)) return true;
  if (t === "---" || t === "***" || t === "___") return true;
  if (
    t.startsWith("<details") ||
    t.startsWith("<div") ||
    t.startsWith("<p") ||
    t === "</div>" ||
    t === "</p>" ||
    t.startsWith("<!--") ||
    t.startsWith("<h1") ||
    t.startsWith("<h2") ||
    t.startsWith("<h3") ||
    t.startsWith("<h4") ||
    t.startsWith("<h5") ||
    t.startsWith("<h6")
  ) {
    return true;
  }
  if (t.startsWith(":::")) return true;
  if (t.startsWith("@[youtube]") || t.startsWith("@[tweet]") || t.startsWith("@[embed]")) return true;

  if (t.startsWith("![")) {
    return !!t.match(/^!\[(.*?)\]\(((?:\([^()\s]*\)|[^)\s])+)(?:\s+"(.*?)"|\s+'(.*?)')?\)(?:\{(.*?)\})?$/);
  }
  return false;
}

/**
 * Parses a standard GFM Markdown string (with CantaBook custom extensions) into Slate AST nodes.
 * Handles headings, tables, code blocks, lists, blockquotes, embeds, alignment, and inline formatting.
 *
 * @param text - The GFM Markdown string to parse.
 * @returns An array of Slate Descendant nodes.
 */
export function markdownToSlate(text: string): Descendant[] {
  if (!text) {
    return [{ type: "paragraph", children: [{ text: "" }] }] as any;
  }

  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const nodes: any[] = [];
  let i = 0;
  let currentAlign: string | undefined = undefined;

  while (i < lines.length) {
    let line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i++;
      continue;
    }

    // Skip HTML Comments (supports multi-line)
    if (trimmed.startsWith("<!--")) {
      let tempIndex = i;
      let foundClosing = false;
      while (tempIndex < lines.length) {
        if (lines[tempIndex].trim().endsWith("-->") || lines[tempIndex].includes("-->")) {
          foundClosing = true;
          break;
        }
        tempIndex++;
      }
      if (foundClosing) {
        i = tempIndex + 1;
        continue;
      }
    }

    // Strip standalone wrapping block HTML tags like <div>, <p> to avoid raw tag rendering
    let lineAlign = currentAlign;
    if (trimmed.startsWith("<div") || trimmed.startsWith("<p") || trimmed === "</div>" || trimmed === "</p>") {
      if (trimmed === "</div>" || trimmed === "</p>") {
        currentAlign = undefined;
        i++;
        continue;
      }
      const cleanMatch = trimmed.match(/^<(div|p)[^>]*>([\s\S]*?)<\/\1>$/i);
      if (cleanMatch) {
        const openingTag = trimmed.match(/^<(div|p)[^>]*>/i)?.[0] || "";
        const alignMatch = openingTag.match(/align=["'](.*?)["']/i);
        if (alignMatch) {
          lineAlign = alignMatch[1];
        }
        line = cleanMatch[2];
      } else {
        const alignMatch = trimmed.match(/align=["'](.*?)["']/i);
        if (alignMatch) {
          currentAlign = alignMatch[1];
        }
        i++;
        continue;
      }
    }

    const alignToApply = lineAlign || currentAlign;

    // HTML Headings (supports multi-line)
    if (
      trimmed.startsWith("<h1") ||
      trimmed.startsWith("<h2") ||
      trimmed.startsWith("<h3") ||
      trimmed.startsWith("<h4") ||
      trimmed.startsWith("<h5") ||
      trimmed.startsWith("<h6")
    ) {
      const level = Number(trimmed[2]);
      const closingTag = `</h${level}>`;
      let headingContent = line;
      let tempIndex = i;
      let foundClosing = false;

      while (tempIndex < lines.length) {
        if (lines[tempIndex].includes(closingTag)) {
          if (tempIndex > i) {
            headingContent += `\n${lines[tempIndex]}`;
          }
          foundClosing = true;
          break;
        }
        if (tempIndex > i) {
          headingContent += `\n${lines[tempIndex]}`;
        }
        tempIndex++;
      }

      if (foundClosing) {
        const closeIdx = headingContent.toLowerCase().indexOf(closingTag);
        const headingInner = headingContent.substring(0, closeIdx);
        const headingTrailing = headingContent.substring(closeIdx + closingTag.length).trim();

        // Extract align from the HTML heading opening tag
        const openingTagMatch = headingInner.match(/^<h[1-6][^>]*>/i);
        const openingTag = openingTagMatch ? openingTagMatch[0] : "";
        const alignMatch = openingTag.match(/align=["'](.*?)["']/i);
        const headingAlign = alignMatch ? alignMatch[1] : undefined;

        const cleanText = headingInner.replace(/^<h[1-6][^>]*>/i, "").trim();

        const type =
          level === 1
            ? "heading-one"
            : level === 2
              ? "heading-two"
              : level === 3
                ? "heading-three"
                : level === 4
                  ? "heading-four"
                  : level === 5
                    ? "heading-five"
                    : "heading-six";

        nodes.push({
          type,
          align: headingAlign || alignToApply,
          children: parseInlines(cleanText),
        });

        if (headingTrailing) {
          nodes.push({
            type: "paragraph",
            align: alignToApply,
            children: parseInlines(headingTrailing),
          });
        }

        i = tempIndex + 1;
        continue;
      }
    }
    // Code Block (``` fences, plus ~~~ fences when they are properly closed)
    const tildeFenceLength = trimmed.match(/^(~{3,})[^~]*$/)?.[1].length ?? 0;
    const isTildeFence =
      tildeFenceLength > 0 &&
      lines.slice(i + 1).some((l) => {
        const m = l.trim().match(/^(~{3,})\s*$/);
        return !!m && m[1].length >= tildeFenceLength;
      });
    if (trimmed.startsWith("```") || isTildeFence) {
      const fenceChar = isTildeFence ? "~" : "`";
      const openFenceMatch = trimmed.match(isTildeFence ? /^(~+)/ : /^(`+)/);
      const openFenceLength = openFenceMatch ? openFenceMatch[1].length : 3;
      const language = trimmed.slice(openFenceLength).trim();
      const closeFenceRegex = new RegExp(`^(${fenceChar}{3,})\\s*$`);
      let codeContent = "";
      i++;
      while (i < lines.length) {
        const currentTrim = lines[i].trim();
        const closeFenceMatch = currentTrim.match(closeFenceRegex);
        if (closeFenceMatch && closeFenceMatch[1].length >= openFenceLength) {
          break;
        }
        codeContent += (codeContent ? "\n" : "") + lines[i];
        i++;
      }
      i++; // skip closing ```
      nodes.push({
        type: "code-block",
        language: language || undefined,
        children: [{ text: unescapeHtml(codeContent) }],
      });
      continue;
    }
    // Stray closing tag left over from malformed/legacy toggles: ignore instead of rendering literal text
    if (trimmed === "</details>") {
      i++;
      continue;
    }

    // HTML details/summary block
    if (trimmed.startsWith("<details>") || trimmed.startsWith("<details ")) {
      let title = "";
      i++;
      if (i < lines.length && lines[i].trim().startsWith("<summary>")) {
        const summaryLine = lines[i].trim();
        const match = summaryLine.match(/<summary>(.*?)<\/summary>/);
        if (match) {
          title = match[1];
        } else {
          title = summaryLine.replace(/<summary>|<\/summary>/g, "").trim();
        }
        i++;
      }

      // Track nesting depth so a nested <details> does not close the outer toggle early
      let detailsContent = "";
      let detailsDepth = 1;
      while (i < lines.length) {
        const currentLine = lines[i];
        const currentTrim = currentLine.trim();
        if (currentTrim.startsWith("<details>") || currentTrim.startsWith("<details ")) {
          detailsDepth++;
        } else if (currentTrim.startsWith("</details>")) {
          detailsDepth--;
          if (detailsDepth === 0) break;
        }
        detailsContent += (detailsContent ? "\n" : "") + currentLine;
        i++;
      }
      i++; // skip closing </details>

      if (title === "Toggle Details") {
        title = "";
      }

      nodes.push({
        type: "toggle-list",
        children: [
          {
            type: "toggle-title",
            children: parseInlines(title || ""),
          },
          {
            type: "toggle-content",
            children: markdownToSlate(detailsContent),
          },
        ],
      });
      continue;
    }

    // Toggle Details Block (legacy format)
    if (trimmed.startsWith(":::toggle")) {
      let title = trimmed.replace(":::toggle", "").trim();
      if (title === "Toggle Details") {
        title = "";
      }
      let toggleContent = "";
      i++;
      while (i < lines.length && !lines[i].trim().startsWith(":::")) {
        toggleContent += (toggleContent ? "\n" : "") + lines[i];
        i++;
      }
      i++; // skip closing :::
      nodes.push({
        type: "toggle-list",
        children: [
          {
            type: "toggle-title",
            children: parseInlines(title || ""),
          },
          {
            type: "toggle-content",
            children: markdownToSlate(toggleContent),
          },
        ],
      });
      continue;
    }

    // Horizontal Rule
    if (trimmed === "---" || trimmed === "***" || trimmed === "___") {
      nodes.push({
        type: "horizontal-rule",
        children: [{ text: "" }],
      });
      i++;
      continue;
    }

    // Block Quote — collect ALL consecutive >, &gt;, or &amp;gt; lines into one node (preserves \n from Enter)
    if (trimmed.startsWith(">") || trimmed.startsWith("&gt;") || trimmed.startsWith("&amp;gt;")) {
      const quoteLines: string[] = [];
      while (i < lines.length) {
        const lTrim = lines[i].trim();
        if (!lTrim.startsWith(">") && !lTrim.startsWith("&gt;") && !lTrim.startsWith("&amp;gt;")) {
          break;
        }
        const cleaned = lines[i].replace(/^\s*(?:>|&gt;|&amp;gt;)\s?/, "");
        quoteLines.push(cleaned);
        i++;
      }
      nodes.push({
        type: "block-quote",
        align: alignToApply,
        children: parseInlines(quoteLines.join("\n")),
      });
      continue;
    }

    // Headings
    if (trimmed.startsWith("#")) {
      if (trimmed.match(/^(#{1,6})\s*$/)) {
        i++;
        continue;
      }
      const match = trimmed.match(/^(#{1,6})\s+(.*)$/);
      if (match) {
        const level = match[1].length;
        const headingText = match[2];
        const type =
          level === 1
            ? "heading-one"
            : level === 2
              ? "heading-two"
              : level === 3
                ? "heading-three"
                : level === 4
                  ? "heading-four"
                  : level === 5
                    ? "heading-five"
                    : "heading-six";
        nodes.push({
          type,
          align: alignToApply,
          children: parseInlines(headingText),
        });
        i++;
        continue;
      }
    }

    // Checklist Item
    const checklistMatch = trimmed.match(/^([-*+])\s+\[([ xX])\]\s*(.*)$/);
    if (checklistMatch) {
      const checked = checklistMatch[2].toLowerCase() === "x";
      const checkText = checklistMatch[3];
      nodes.push({
        type: "check-list-item",
        checked,
        align: alignToApply,
        children: parseInlines(checkText),
      });
      i++;
      continue;
    }

    // Helper to test if a line starts with or is a bullet list marker
    const isBulletMarker = (str: string) => {
      const t = str.trim();
      return (
        t.startsWith("- ") ||
        t === "-" ||
        t.startsWith("* ") ||
        t === "*" ||
        t.startsWith("+ ") ||
        t === "+" ||
        t.startsWith("• ") ||
        t === "•" ||
        t.startsWith("&bull; ") ||
        t === "&bull;"
      );
    };

    // Bulleted List — produces flat bulleted-list-item nodes
    if (isBulletMarker(trimmed)) {
      while (i < lines.length) {
        const currentTrimmed = lines[i].trim();
        // Break if it is a checklist item to prevent it from being parsed as simple list item
        if (currentTrimmed.match(/^([-*+•]|&bull;)\s+\[([ xX])\]/)) {
          break;
        }
        if (isBulletMarker(currentTrimmed)) {
          let itemText = currentTrimmed.replace(/^([-*+•]|&bull;)\s*/, "").trim();

          // Handle standalone bullet marker on line i followed by text on line i+1
          if (!itemText && i + 1 < lines.length) {
            const nextLineTrimmed = lines[i + 1].trim();
            if (
              nextLineTrimmed &&
              !isBulletMarker(nextLineTrimmed) &&
              !nextLineTrimmed.startsWith("#") &&
              !nextLineTrimmed.startsWith(">") &&
              !nextLineTrimmed.startsWith("```") &&
              !/^\d+[.\)](\s|$)/.test(nextLineTrimmed)
            ) {
              itemText = nextLineTrimmed;
              i++;
            }
          }

          nodes.push({
            type: "bulleted-list-item",
            align: alignToApply,
            children: parseInlines(itemText),
          });
          i++;
        } else {
          break;
        }
      }
      continue;
    }

    // Numbered List â€” now produces flat numbered-list-item nodes (no wrapper)
    if (/^\d+[.\)](\s|$)/.test(trimmed)) {
      let isFirstNumbered = true;
      while (i < lines.length && /^\d+[.\)](\s|$)/.test(lines[i].trim())) {
        const rawLine = lines[i].trim();
        const itemNumber = Number.parseInt(rawLine, 10);
        const itemText = rawLine.replace(/^\d+[.\)]\s*/, "").trim();
        nodes.push({
          type: "numbered-list-item",
          align: alignToApply,
          // Only the first item of a sequence carries its start number (default 1)
          ...(isFirstNumbered && itemNumber !== 1 ? { start: itemNumber } : {}),
          children: parseInlines(itemText),
        });
        isFirstNumbered = false;
        i++;
      }
      continue;
    }

    // HTML Table
    if (trimmed.startsWith("<table")) {
      const colWidthsMatch = trimmed.match(/colWidths="([^"]+)"/);
      const colWidths = colWidthsMatch ? colWidthsMatch[1].split(",").map(Number) : undefined;

      const rows: any[] = [];
      i++; // Move past <table...>

      while (i < lines.length) {
        const rowLine = lines[i].trim();
        if (rowLine.startsWith("</table>")) {
          i++;
          break;
        }

        if (rowLine.startsWith("<tr") || rowLine.startsWith("<tr>")) {
          const cells: any[] = [];
          i++; // Move past <tr>

          while (i < lines.length) {
            const cellLine = lines[i].trim();
            if (cellLine.startsWith("</tr>") || cellLine.startsWith("  </tr>")) {
              i++;
              break;
            }

            // Parse <td...>Content</td>
            const cellMatch = cellLine.match(/^<td([^>]*)>([\s\S]*?)<\/td>/);
            if (cellMatch) {
              const attrsStr = cellMatch[1];
              const cellContent = cellMatch[2];

              const bgMatch = attrsStr.match(/background="([^"]+)"/);
              const textMatch = attrsStr.match(/textColor="([^"]+)"/);

              const background = bgMatch ? bgMatch[1] : undefined;
              const textColor = textMatch ? textMatch[1] : undefined;

              cells.push({
                type: "table-cell",
                background,
                textColor,
                children: parseInlines(cellContent),
              });
            }
            i++;
          }

          rows.push({
            type: "table-row",
            children: cells,
          });
        } else {
          i++;
        }
      }

      if (rows.length > 0) {
        nodes.push({
          type: "table",
          colWidths,
          children: rows,
        });
      }
      continue;
    }

    // Table
    if (trimmed.startsWith("|")) {
      const rows: any[] = [];
      let tableLineIndex = 0;
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        const rowLine = lines[i].trim();
        if (isTableSeparatorRow(rowLine, tableLineIndex)) {
          i++;
          tableLineIndex++;
          continue;
        }
        tableLineIndex++;
        const cells = splitTableRow(rowLine);
        const cellNodes = cells.map((cellText) => {
          const spanMatch = matchWrappingSpan(cellText);
          let background: string | undefined = undefined;
          let textColor: string | undefined = undefined;
          let parsedContent = cellText;

          if (spanMatch) {
            const attrsStr = spanMatch.attrs;
            parsedContent = spanMatch.inner;
            const bgMatch = attrsStr.match(/data-bg="([^"]+)"/i);
            const textMatch = attrsStr.match(/data-text="([^"]+)"/i);
            if (bgMatch) background = bgMatch[1];
            if (textMatch) textColor = textMatch[1];
          }

          return {
            type: "table-cell",
            background,
            textColor,
            children: parseInlines(parsedContent),
          };
        });
        rows.push({
          type: "table-row",
          children: cellNodes,
        });
        i++;
      }

      // Check if next line is column widths comment
      let colWidths: number[] | undefined = undefined;
      if (i < lines.length) {
        const nextLine = lines[i].trim();
        const widthsMatch = nextLine.match(/^<!--\s*colWidths:\s*([^>]+?)\s*-->/);
        if (widthsMatch) {
          colWidths = widthsMatch[1].split(",").map(Number);
          i++; // skip the comment line
        }
      }

      if (rows.length > 0) {
        // Rows with fewer cells than the widest row are padded, so no content is dropped and every row has the
        // same number of cells (the editor and the serializer both assume a rectangular table).
        const maxCols = Math.max(...rows.map((r) => r.children.length));
        for (const row of rows) {
          while (row.children.length < maxCols) {
            row.children.push({ type: "table-cell", children: [{ text: "" }] });
          }
        }
        nodes.push({
          type: "table",
          colWidths,
          children: rows,
        });
      }
      continue;
    }

    // YouTube Embed
    if (trimmed.startsWith("@[youtube]")) {
      const match = trimmed.match(/^@\[youtube\]\((.*?)\)$/);
      if (match) {
        nodes.push({
          type: "youtube",
          videoId: match[1],
          url: `https://www.youtube.com/watch?v=${match[1]}`,
          children: [{ text: "" }],
        });
        i++;
        continue;
      }
    }

    // Tweet Embed & Link Embed Card
    if (trimmed.startsWith("@[tweet]") || trimmed.startsWith("@[embed]")) {
      const match = trimmed.match(/^@\[(?:tweet|embed)\]\((.*?)\)(?:\{(.*?)\})?$/);
      if (match) {
        const rawUrl = match[1];
        const attrStr = match[2] || "";
        const cleanUrl = sanitizeUrl(rawUrl);

        // Check if URL is a Twitter/X status URL
        const isTwitterStatus = /https?:\/\/(?:twitter\.com|x\.com)\/([a-zA-Z0-9_]+)\/status\/(\d+)/i.test(cleanUrl);

        if (isTwitterStatus) {
          const heightMatch = attrStr.match(/h=(\d+)/);
          nodes.push({
            type: "tweet",
            url: cleanUrl,
            savedHeight: heightMatch ? Number.parseInt(heightMatch[1], 10) : undefined,
            tweetText: 'Click "View live" to see the live tweet.',
            children: [{ text: "" }],
          });
          i++;
          continue;
        }

        const attrs: Record<string, string> = {};
        if (attrStr) {
          const pairs = attrStr.matchAll(/(\w+)="((?:[^"\\]|\\.)*)"/g);
          for (const pairMatch of pairs) {
            const key = pairMatch[1];
            const val = pairMatch[2].replace(/\\"/g, '"').replace(/\\\\/g, "\\");
            attrs[key] = val;
          }
        }

        nodes.push({
          type: "embed-card",
          url: cleanUrl,
          title: attrs.title || rawUrl,
          description: attrs.desc || "",
          image: attrs.image ? sanitizeUrl(attrs.image) : "",
          author: attrs.author || "",
          domain: attrs.domain || "",
          siteName: attrs.site || "",
          children: [{ text: "" }],
        });
        i++;
        continue;
      }
    }

    // Linked Image: [![alt](image-url)](link-url) or [![alt](image-url)](link-url){width=...,align=...}
    if (trimmed.startsWith("[![")) {
      const match = trimmed.match(
        /^\[!\[([^\]]*)\]\(((?:\([^()\s]*\)|[^)\s])+)(?:\s+"(.*?)"|\s+'(.*?)')?\)?\]\(((?:\([^()]*\)|[^)])+)\)(?:\{(.*?)\})?$/,
      );
      if (match) {
        const altText = match[1] ? match[1].trim() : undefined;
        const url = match[2];
        const caption = match[3] || match[4] || altText || undefined;
        const linkUrl = match[5];
        const attrStr = match[6] || "";
        const attrs: Record<string, string> = {};
        if (attrStr) {
          attrStr.split(/[\s,]+/).forEach((pair) => {
            const [k, v] = pair.split("=");
            if (k && v) attrs[k.trim()] = v.trim();
          });
        }
        nodes.push({
          type: "image",
          url,
          linkUrl,
          caption: caption || attrs.caption || undefined,
          width: attrs.width ? Number(attrs.width) : undefined,
          align: (attrs.align as any) || alignToApply || undefined,
          naturalWidth: attrs.nw ? Number(attrs.nw) : undefined,
          naturalHeight: attrs.nh ? Number(attrs.nh) : undefined,
          children: [{ text: "" }],
        });
        i++;
        continue;
      }
    }

    // Image: ![alt](url) or ![alt](url "caption") or ![alt](url){width=...,align=...}
    if (trimmed.startsWith("![")) {
      const match = trimmed.match(/^!\[(.*?)\]\(((?:\([^()\s]*\)|[^)\s])+)(?:\s+"(.*?)"|\s+'(.*?)')?\)(?:\{(.*?)\})?$/);
      if (match) {
        const altText = match[1] ? match[1].trim() : undefined;
        const url = match[2];
        const caption = match[3] || match[4] || altText || undefined;
        const attrStr = match[5] || "";
        const attrs: Record<string, string> = {};
        if (attrStr) {
          attrStr.split(/[\s,]+/).forEach((pair) => {
            const [k, v] = pair.split("=");
            if (k && v) attrs[k.trim()] = v.trim();
          });
        }
        nodes.push({
          type: "image",
          url,
          caption: caption || attrs.caption || undefined,
          width: attrs.width ? Number(attrs.width) : undefined,
          align: (attrs.align as any) || alignToApply || undefined,
          // nw/nh: natural pixel dimensions for skeleton placeholder
          naturalWidth: attrs.nw ? Number(attrs.nw) : undefined,
          naturalHeight: attrs.nh ? Number(attrs.nh) : undefined,
          children: [{ text: "" }],
        });
        i++;
        continue;
      }
    }

    // Custom image container (legacy format): :::img{width=500,align=left}
    if (trimmed.startsWith(":::img")) {
      const attrStr = trimmed.match(/^:::img\{(.*?)\}/)?.[1] || "";
      const attrs: Record<string, string> = {};
      attrStr.split(",").forEach((pair) => {
        const [k, v] = pair.split("=");
        if (k && v) attrs[k.trim()] = v.trim();
      });

      // Next line should be the image markdown
      i++;
      let imageContent = "";
      while (i < lines.length && !lines[i].trim().startsWith(":::")) {
        imageContent += (imageContent ? "\n" : "") + lines[i];
        i++;
      }
      i++; // skip closing :::

      const imgMatch = imageContent.trim().match(/^!\[(.*?)\]\((\S+)(?:\s+"(.*?)")?\)$/);
      if (imgMatch) {
        nodes.push({
          type: "image",
          url: imgMatch[2],
          caption: imgMatch[3] || attrs.caption || undefined,
          width: attrs.width ? Number(attrs.width) : undefined,
          align: (attrs.align as any) || alignToApply || undefined,
          children: [{ text: "" }],
        });
      } else {
        // If no image inside, still push the content as paragraph
        nodes.push({
          type: "paragraph",
          align: alignToApply,
          children: [{ text: imageContent || "" }],
        });
      }
      continue;
    }

    // Standard Paragraph with multi-line merge
    let accumulatedText = line;
    let tempIndex = i + 1;
    while (tempIndex < lines.length) {
      const nextLine = lines[tempIndex];
      if (isBlockStart(nextLine)) {
        break;
      }
      accumulatedText += ` ${nextLine.trim()}`;
      tempIndex++;
    }

    const trimmedAccumulated = accumulatedText.trim();
    // Check if the accumulated paragraph is actually a single standalone image
    const singleMdLinkedImgMatch = trimmedAccumulated.match(
      /^\[!\[([^\]]*)\]\(((?:\([^()\s]*\)|[^)\s])+)(?:\s+"(.*?)"|\s+'(.*?)')?\)?\]\(((?:\([^()]*\)|[^)])+)\)(?:\{(.*?)\})?$/,
    );
    const singleHtmlLinkedImgMatch = trimmedAccumulated.match(/^<a\s+([^>]*?)>\s*<img\s+([^>]*?)\/?>\s*<\/a>$/i);
    const singleMdImgMatch = trimmedAccumulated.match(/^!\[(.*?)\]\(((?:\([^()\s]*\)|[^)\s])+)(?:\s+"(.*?)"|\s+'(.*?)')?\)(?:\{(.*?)\})?$/);
    const singleHtmlImgMatch =
      trimmedAccumulated.match(/^<img\s+([^>]*?)\/?>$/i) || trimmedAccumulated.match(/^<img\s+([^>]*?)>\s*<\/img>$/i);

    if (singleMdLinkedImgMatch) {
      const url = singleMdLinkedImgMatch[2];
      const caption = singleMdLinkedImgMatch[3] || singleMdLinkedImgMatch[4] || undefined;
      const linkUrl = singleMdLinkedImgMatch[5];
      const attrStr = singleMdLinkedImgMatch[6] || "";
      const attrs: Record<string, string> = {};
      if (attrStr) {
        attrStr.split(/[\s,]+/).forEach((pair) => {
          const [k, v] = pair.split("=");
          if (k && v) attrs[k.trim()] = v.trim();
        });
      }
      nodes.push({
        type: "image",
        url,
        linkUrl,
        caption: caption || attrs.caption || undefined,
        width: attrs.width ? Number(attrs.width) : undefined,
        align: (attrs.align as any) || alignToApply || undefined,
        naturalWidth: attrs.nw ? Number(attrs.nw) : undefined,
        naturalHeight: attrs.nh ? Number(attrs.nh) : undefined,
        children: [{ text: "" }],
      });
    } else if (singleHtmlLinkedImgMatch) {
      const aAttrStr = singleHtmlLinkedImgMatch[1];
      const imgAttrStr = singleHtmlLinkedImgMatch[2];
      const hrefMatch = aAttrStr.match(/href=["']([\s\S]*?)["']/i);
      const srcMatch = imgAttrStr.match(/src=["']([\s\S]*?)["']/i);
      const altMatch = imgAttrStr.match(/alt=["']([\s\S]*?)["']/i);
      const widthMatch = imgAttrStr.match(/width=["']([\s\S]*?)["']/i);
      const heightMatch = imgAttrStr.match(/height=["']([\s\S]*?)["']/i);
      const nwMatch = imgAttrStr.match(/data-nw=["']([\s\S]*?)["']/i);
      const nhMatch = imgAttrStr.match(/data-nh=["']([\s\S]*?)["']/i);
      const isExplicitlyInline = /data-inline=["']true["']/i.test(imgAttrStr);

      if (srcMatch) {
        if (isExplicitlyInline) {
          nodes.push({
            type: "paragraph",
            align: alignToApply,
            children: parseInlines(accumulatedText),
          });
        } else {
          const width = widthMatch ? Number(widthMatch[1]) : undefined;
          nodes.push({
            type: "image",
            url: srcMatch[1],
            linkUrl: hrefMatch ? hrefMatch[1] : undefined,
            caption: altMatch ? altMatch[1] : undefined,
            width: width,
            align: alignToApply || undefined,
            naturalWidth: nwMatch ? Number(nwMatch[1]) : undefined,
            naturalHeight: nhMatch ? Number(nhMatch[1]) : undefined,
            children: [{ text: "" }],
          });
        }
      } else {
        nodes.push({
          type: "paragraph",
          align: alignToApply,
          children: parseInlines(accumulatedText),
        });
      }
    } else if (singleMdImgMatch) {
      const url = singleMdImgMatch[2];
      const caption = singleMdImgMatch[3] || singleMdImgMatch[4] || undefined;
      const attrStr = singleMdImgMatch[5] || "";
      const attrs: Record<string, string> = {};
      if (attrStr) {
        attrStr.split(/[\s,]+/).forEach((pair) => {
          const [k, v] = pair.split("=");
          if (k && v) attrs[k.trim()] = v.trim();
        });
      }
      nodes.push({
        type: "image",
        url,
        caption: caption || attrs.caption || undefined,
        width: attrs.width ? Number(attrs.width) : undefined,
        align: (attrs.align as any) || alignToApply || undefined,
        children: [{ text: "" }],
      });
    } else if (singleHtmlImgMatch) {
      const attrStr = singleHtmlImgMatch[1];
      const srcMatch = attrStr.match(/src=["']([\s\S]*?)["']/i);
      const altMatch = attrStr.match(/alt=["']([\s\S]*?)["']/i);
      const widthMatch = attrStr.match(/width=["']([\s\S]*?)["']/i);
      const heightMatch = attrStr.match(/height=["']([\s\S]*?)["']/i);
      // Our custom data-inline="true" marker means this img was serialized from an
      // inline-image node â€” always parse as paragraph with inline-image, never block.
      const isExplicitlyInline = /data-inline=["']true["']/i.test(attrStr);

      if (srcMatch) {
        if (isExplicitlyInline) {
          // Re-create as inline-image inside a paragraph (preserves inline context)
          nodes.push({
            type: "paragraph",
            align: alignToApply,
            children: parseInlines(accumulatedText),
          });
        } else {
          const width = widthMatch ? Number(widthMatch[1]) : undefined;
          nodes.push({
            type: "image",
            url: srcMatch[1],
            caption: altMatch ? altMatch[1] : undefined,
            width: width,
            align: alignToApply || undefined,
            children: [{ text: "" }],
          });
        }
      } else {
        nodes.push({
          type: "paragraph",
          align: alignToApply,
          children: parseInlines(accumulatedText),
        });
      }
    } else {
      nodes.push({
        type: "paragraph",
        align: alignToApply,
        children: parseInlines(accumulatedText),
      });
    }
    i = tempIndex;
  }

  function cleanBlockNodes(nodes: any[]): any[] {
    if (!nodes || nodes.length === 0) return [];
    const isBlockContainer = (n: any) =>
      n &&
      [
        "image",
        "youtube",
        "tweet",
        "embed-card",
        "table",
        "code-block",
        "horizontal-rule",
        "toggle-list",
        "heading-one",
        "heading-two",
        "heading-three",
        "heading-four",
        "heading-five",
        "heading-six",
        "block-quote",
      ].includes(n.type);

    const isBlankParagraph = (n: any) =>
      n && n.type === "paragraph" && n.children?.length === 1 && (!n.children[0].text || n.children[0].text.trim() === "");

    // Twitter/X assets are matched by hostname, not by substring: "abs"/"pbs" used to delete any image whose
    // URL merely contained those letters (e.g. /labs/, abstract.png).
    const twitterAssetUrl = /https?:\/\/(?:[\w-]+\.)*(?:twimg\.com|twitter\.com|x\.com)(?=[/:?#]|$)/i;
    const twitterStatusUrl = /https?:\/\/(?:twitter\.com|x\.com)\/[a-zA-Z0-9_]+\/status\/\d+/i;
    const xLogoFile = /(?:^|[/._=-])x-logo\b/i;

    const isTwitterImage = (n: any) => {
      if (!n || n.type !== "image") return false;
      const url = n.url || "";
      const linkUrl = n.linkUrl || "";
      let decodedUrl = url;
      let decodedLinkUrl = linkUrl;
      try {
        decodedUrl = decodeURIComponent(url);
        decodedLinkUrl = decodeURIComponent(linkUrl);
      } catch {}

      return (
        twitterAssetUrl.test(decodedUrl) ||
        xLogoFile.test(decodedUrl) ||
        twitterStatusUrl.test(decodedLinkUrl) ||
        xLogoFile.test(decodedLinkUrl)
      );
    };

    const isTwitterProfileParagraph = (n: any) => {
      if (!n || n.type !== "paragraph" || !n.children) return false;
      const text = n.children
        .map((c: any) => c.text || "")
        .join(" ")
        .trim();
      return /^\s*\[?[^\]]+\]?\(\s*https?:\/\/(?:twitter\.com|x\.com)\/[a-zA-Z0-9_]+\/?\s*\)\s*$/i.test(text);
    };

    const seenTweetUrls = new Set<string>();

    const result: any[] = [];
    for (let i = 0; i < nodes.length; i++) {
      const curr = nodes[i];
      const prev = result[result.length - 1];
      const next = nodes[i + 1];

      // Drop any image node that comes from Twitter CDN or Twitter proxy
      if (isTwitterImage(curr)) {
        continue;
      }

      // Drop standalone Twitter profile link paragraphs adjacent to tweet embeds (e.g. [A0x](https://x.com/a0xbot))
      if (isTwitterProfileParagraph(curr) && (prev?.type === "tweet" || next?.type === "tweet")) {
        continue;
      }

      // Deduplicate tweet nodes with identical URLs
      if (curr && curr.type === "tweet" && curr.url) {
        if (seenTweetUrls.has(curr.url)) {
          continue;
        }
        seenTweetUrls.add(curr.url);
      }

      if (isBlankParagraph(curr)) {
        if (isBlankParagraph(prev)) {
          continue;
        }
        if (prev && isBlockContainer(prev)) {
          continue;
        }
        if (next && isBlockContainer(next)) {
          continue;
        }
      }
      result.push(curr);
    }
    return result;
  }

  if (nodes.length === 0) {
    return [{ type: "paragraph", children: [{ text: "" }] }] as any;
  }

  return stripTrailingBlankParagraphs(cleanBlockNodes(nodes)) as any;
}

function stripTrailingBlankParagraphs(nodes: any[]): any[] {
  const isBlank = (n: any) =>
    n && n.type === "paragraph" && n.children?.length === 1 && typeof n.children[0].text === "string" && n.children[0].text.trim() === "";
  const result = [...nodes];
  while (result.length > 1 && isBlank(result[result.length - 1])) {
    result.pop();
  }
  return result;
}

// ─── Docs Specific Groupings ────────────────────────────────────────────────
function getNodeText(node: any): string {
  if (!node) return "";
  if (typeof node.text === "string") return node.text;
  if (Array.isArray(node.children)) {
    return node.children.map(getNodeText).join("");
  }
  return "";
}

function isDocCard(node: any): boolean {
  if (node.type !== "paragraph" || !node.children) return false;
  const first = node.children[0];
  const second = node.children[1];
  if (first?.type !== "link") return false;
  if (node.children.length === 1) return true;
  if (node.children.length === 2 && typeof second.text === "string" && second.text.trim().startsWith("-")) {
    return true;
  }
  return false;
}

function groupCodeTabs(nodes: any[]): any[] {
  const result: any[] = [];
  let pendingListItems: any[] = [];

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];

    if (node.type === "bulleted-list-item") {
      pendingListItems.push(node);
      continue;
    }

    if (node.type === "code-block" && pendingListItems.length > 0) {
      const tabs = pendingListItems.map((item) => getNodeText(item).trim()).filter(Boolean);
      result.push({
        type: "code-tabs",
        tabs,
        children: [{ text: "" }],
      });
      pendingListItems = [];
      result.push(node);
      continue;
    }

    if (pendingListItems.length > 0) {
      result.push(...pendingListItems);
      pendingListItems = [];
    }

    result.push(node);
  }

  if (pendingListItems.length > 0) {
    result.push(...pendingListItems);
  }

  return result;
}

function groupDocCardGrids(nodes: any[]): any[] {
  const result: any[] = [];
  let currentGrid: any[] = [];

  const flushGrid = () => {
    if (currentGrid.length > 0) {
      if (currentGrid.length === 1) {
        result.push(currentGrid[0]);
      } else {
        result.push({
          type: "doc-card-grid",
          children: currentGrid,
        });
      }
      currentGrid = [];
    }
  };

  for (const node of nodes) {
    if (isDocCard(node)) {
      currentGrid.push(node);
    } else {
      flushGrid();
      result.push(node);
    }
  }
  flushGrid();
  return result;
}

export function toSlateNodes(content?: string | null, enableDocs = true): Descendant[] {
  if (!content) {
    return [{ type: "paragraph", children: [{ text: "" }] }] as any;
  }
  const trimmed = content.trim();
  let nodes: Descendant[];
  if (trimmed.startsWith("[")) {
    try {
      nodes = JSON.parse(trimmed) as Descendant[];
    } catch (_) {
      nodes = markdownToSlate(trimmed);
    }
  } else {
    nodes = markdownToSlate(trimmed);
  }

  if (enableDocs) {
    return groupDocCardGrids(groupCodeTabs(nodes));
  }
  return nodes;
}

// ─── Component Elements ─────────────────────────────────────────────────────

function extractText(children: any[]): string {
  if (!children) return "";
  return children
    .map((c) => {
      if (typeof c.text === "string") return c.text;
      if (Array.isArray(c.children)) return extractText(c.children);
      return "";
    })
    .join("");
}

const HeadingComponent = ({ tag, className, attributes, children, element, style }: any) => {
  const text = extractText(element.children || []);
  const id = text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

  const Tag = tag;

  return (
    <Tag id={id || undefined} className={`${className} scroll-mt-24`} style={style} {...attributes}>
      {children}
    </Tag>
  );
};

function getDocCardMeta(title: string, url: string, desc: string) {
  const t = `${title} ${url} ${desc}`.toLowerCase();
  const name = title.trim().toLowerCase();
  if (name === "writers") {
    return {
      icon: PenLine,
      iconClass: "text-teal bg-teal/10 border-teal/20",
    };
  }
  if (name === "readers") {
    return {
      icon: BookOpen,
      iconClass: "text-green bg-green/10 border-green/20",
    };
  }
  if (name === "general") {
    return {
      icon: Compass,
      iconClass: "text-blue bg-blue/10 border-blue/20",
    };
  }
  if (t.includes("101") || t.includes("primer") || t.includes("guide")) {
    return {
      icon: BookOpen,
      iconClass: "text-blue bg-blue/10 border-blue/20",
    };
  }
  if (t.includes("api") || t.includes("developer") || t.includes("sdk") || t.includes("code")) {
    return {
      icon: Code2,
      iconClass: "text-purple bg-purple/10 border-purple/20",
    };
  }
  if (t.includes("setting") || t.includes("config")) {
    return {
      icon: Settings2,
      iconClass: "text-orange bg-orange/10 border-orange/20",
    };
  }
  return {
    icon: BookOpen,
    iconClass: "text-muted-foreground bg-muted border-border",
  };
}

const ParagraphComponent = ({ attributes, children, element, style }: any) => {
  const docsEnabled = useContext(DocsFeaturesContext);
  const firstChild = element.children?.[0];
  const secondChild = element.children?.[1];

  if (
    docsEnabled &&
    firstChild?.type === "link" &&
    (!secondChild || (element.children.length === 2 && typeof secondChild.text === "string" && secondChild.text.trim().startsWith("-")))
  ) {
    const linkUrl = firstChild.url || "#";
    const titleText = (firstChild.children || []).map((c: any) => c.text || "").join("") || "Documentation";
    const descText = secondChild ? secondChild.text.replace(/^\s*[-–—]\s*/, "").trim() : "";
    const isExternal = linkUrl.startsWith("http://") || linkUrl.startsWith("https://");

    const meta = getDocCardMeta(titleText, linkUrl, descText);
    const IconComp = meta.icon;

    return (
      <div {...attributes} className="markdown-doc-card not-prose">
        <a
          href={sanitizeUrl(linkUrl)}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          className="markdown-doc-card-link"
        >
          <div className={`markdown-doc-card-icon-wrapper ${meta.iconClass}`}>
            <IconComp className="w-4.5 h-4.5" />
          </div>
          <div className="markdown-doc-card-content">
            <div className="markdown-doc-card-title">
              <span className="truncate">{titleText}</span>
              {isExternal && (
                <ArrowUpRight className="w-3.5 h-3.5 text-muted-foreground opacity-50 group-hover:opacity-100 transition-opacity shrink-0" />
              )}
            </div>
            {descText && <div className="markdown-doc-card-desc">{descText}</div>}
          </div>
        </a>
        <div style={{ display: "none" }}>{children}</div>
      </div>
    );
  }

  return (
    <p className="markdown-p" style={style} {...attributes}>
      {children}
    </p>
  );
};

const BulletedListItemComponent = ({ attributes, children }: any) => (
  <div {...attributes} className="markdown-bullet-item-container">
    <span contentEditable={false} className="markdown-bullet-item-bullet-wrapper">
      <span className="markdown-bullet-item-bullet-dot" />
    </span>
    <span className="markdown-bullet-item-content">{children}</span>
  </div>
);

const NumberedListItemComponent = ({ attributes, children, element }: any) => {
  const editor = useSlateStatic();
  const number = useMemo(() => {
    try {
      const path = ReactEditor.findPath(editor as any, element);
      const idx = path[path.length - 1];
      let sequenceStart = idx;
      let startNode: any = element;
      for (let j = idx - 1; j >= 0; j--) {
        const sibPath = [...path.slice(0, -1), j];
        if (!Editor.hasPath(editor, sibPath)) break;
        const [sib] = Editor.node(editor, sibPath);
        if ((sib as any).type !== "numbered-list-item") break;
        sequenceStart = j;
        startNode = sib;
      }
      return (typeof startNode.start === "number" ? startNode.start : 1) + idx - sequenceStart;
    } catch (_) {
      return 1;
    }
  }, [editor, element]);

  return (
    <div {...attributes} className="markdown-number-item-container">
      <span contentEditable={false} className="markdown-number-item-number-wrapper">
        {number}.
      </span>
      <span className="markdown-number-item-content">{children}</span>
    </div>
  );
};

const CheckListItemComponent = ({ attributes, children, element }: any) => (
  <div {...attributes} className="markdown-check-item-container">
    <span contentEditable={false} className="markdown-check-item-checkbox-wrapper">
      <input
        type="checkbox"
        checked={!!element.checked}
        disabled={true}
        readOnly={true}
        className="markdown-check-item-checkbox cursor-default"
      />
    </span>
    <span className={`markdown-check-item-content ${element.checked ? "markdown-check-item-content-checked" : ""}`}>{children}</span>
  </div>
);

const ListItemComponent = ({ attributes, children, style }: any) => {
  const listType = useContext(ListContext);

  if (listType === "bullet") {
    return (
      <div className="markdown-legacy-list-item" style={style} {...attributes}>
        <span contentEditable={false} className="markdown-bullet-item-bullet-wrapper">
          <span className="markdown-bullet-item-bullet-dot" />
        </span>
        <span className="markdown-bullet-item-content">{children}</span>
      </div>
    );
  }

  if (listType === "number") {
    return (
      <div className="markdown-legacy-list-item numbered-item" style={style} {...attributes}>
        <span contentEditable={false} className="numbered-item-marker markdown-legacy-number-marker" />
        <span className="markdown-number-item-content">{children}</span>
      </div>
    );
  }

  return (
    <div className="markdown-legacy-list-item" style={style} {...attributes}>
      {children}
    </div>
  );
};

const BlockQuoteComponent = ({ attributes, children, style }: any) => (
  <blockquote className="markdown-blockquote" style={style} {...attributes}>
    {children}
  </blockquote>
);

const CodeBlockComponent = ({ attributes, children, element }: any) => {
  const [copied, setCopied] = useState(false);
  const rawText = useMemo(() => {
    return (element.children || []).map((c: any) => c.text || "").join("\n");
  }, [element.children]);

  const lang = element.language || "auto";

  const highlightResult = useMemo(() => {
    if (!rawText) return { language: "plaintext", html: "" };

    let finalLang = lang === "auto" || !lang ? "javascript" : lang;
    if (finalLang === "html") finalLang = "markup";

    let grammar = Prism.languages[finalLang];
    if (!grammar) {
      finalLang = "javascript";
      grammar = Prism.languages.javascript;
    }

    try {
      const html = Prism.highlight(rawText, grammar, finalLang);
      return {
        language: finalLang === "markup" ? "html" : finalLang,
        html,
      };
    } catch (_) {
      return {
        language: "plaintext",
        html: escapeHtml(rawText),
      };
    }
  }, [rawText, lang]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(rawText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [rawText]);

  return (
    <div {...attributes} className="code-block-container">
      <div className="code-block-header">
        <div className="flex items-center gap-2">
          <span className="code-block-title">Code</span>
          <span className="code-block-lang">{highlightResult.language}</span>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="code-block-copy-btn flex items-center gap-1.5 cursor-pointer"
          data-testid="code-block-copy-btn"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 code-block-copy-success" />
              <span className="code-block-copy-success text-xs">Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              <span className="text-xs">Copy</span>
            </>
          )}
        </button>
      </div>

      <pre className="code-block-pre custom-scrollbar" suppressHydrationWarning>
        <code
          className={`hljs language-${highlightResult.language}`}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtmlForCode(highlightResult.html),
          }}
        />
      </pre>

      <div style={{ display: "none" }}>{children}</div>
    </div>
  );
};

const CodeTabsComponent = ({ attributes, children, element }: any) => {
  const [activeTab, setActiveTab] = useState(0);
  const tabs: string[] = element.tabs || [];

  return (
    <div {...attributes} className="markdown-code-tabs not-prose">
      {tabs.map((tab: string, idx: number) => {
        const isActive = idx === activeTab;
        return (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(idx)}
            className={`markdown-code-tab-btn ${isActive ? "markdown-code-tab-btn-active" : ""}`}
          >
            {tab}
          </button>
        );
      })}
      <div style={{ display: "none" }}>{children}</div>
    </div>
  );
};

const HorizontalRuleComponent = ({ attributes, children }: any) => (
  <div {...attributes} className="markdown-hr-container">
    <div contentEditable={false}>
      <hr className="markdown-hr" />
    </div>
    {children}
  </div>
);

const TableComponent = ({ attributes, children, element }: any) => {
  const colWidths = element.colWidths;
  return (
    <div className="markdown-table-container custom-scrollbar">
      <table
        className="markdown-table"
        style={{
          width: colWidths ? colWidths.reduce((a: number, b: number) => a + b, 0) : "100%",
          maxWidth: "none",
        }}
        {...attributes}
      >
        {colWidths && (
          <colgroup>
            {colWidths.map((w: number, idx: number) => (
              <col key={idx} style={{ width: w }} />
            ))}
          </colgroup>
        )}
        <tbody className="divide-y divide-border">{children}</tbody>
      </table>
    </div>
  );
};

const TableCellComponent = ({ attributes, children, element }: any) => {
  const backgroundClass = element.background ? CELL_BG_COLOR_MAP[element.background] || element.background : "";
  const textColorClass = element.textColor ? CELL_TEXT_COLOR_MAP[element.textColor] || "" : "";

  return (
    <td
      className={`markdown-td ${backgroundClass} ${textColorClass}`}
      style={{
        ...attributes?.style,
        wordBreak: "break-word",
      }}
      {...attributes}
    >
      {children}
    </td>
  );
};

// Non-text blocks that count as real content even though they carry no text.
const CONTENT_BLOCK_TYPES = new Set(["image", "inline-image", "tweet", "youtube", "embed-card", "table", "code-block", "horizontal-rule"]);

function isNodeContentEmpty(node: any): boolean {
  if (!node) return true;
  if (typeof node.text === "string") return node.text.trim() === "";
  if (CONTENT_BLOCK_TYPES.has(node.type)) return false;
  if (Array.isArray(node.children)) return node.children.every(isNodeContentEmpty);
  return false;
}

const ToggleListComponent = ({ attributes, children, element }: any) => {
  // Same default as the editor: collapsed
  const [isOpen, setIsOpen] = useState(false);
  const childrenArray = React.Children.toArray(children);
  const titleChild = childrenArray[0];
  const contentChild = childrenArray[1];

  // Nothing to show (no title AND no content) -> do not render the toggle at all
  if (isNodeContentEmpty(element?.children?.[0]) && isNodeContentEmpty(element?.children?.[1])) {
    return (
      <div {...attributes} style={{ display: "none" }} aria-hidden="true">
        {children}
      </div>
    );
  }

  return (
    <div {...attributes} className="markdown-toggle-list">
      <div className="markdown-toggle-header">
        <button type="button" contentEditable={false} className="markdown-toggle-btn cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            className={`fill-muted-foreground markdown-toggle-icon ${isOpen ? "rotated" : ""}`}
          >
            <path d="M2.5 1L8 5L2.5 9V1Z" />
          </svg>
        </button>

        <div className="flex-1 min-w-0">{titleChild}</div>
      </div>

      <div style={{ display: isOpen ? "block" : "none" }} className="markdown-toggle-content-container">
        {contentChild}
      </div>
    </div>
  );
};

const ImageComponent = ({ attributes, children, element }: any) => {
  const align = element.align || "center";
  const alignClass = align === "left" ? "mr-auto" : align === "right" ? "ml-auto" : "mx-auto";
  const displayWidth = element.width ? `${element.width}%` : element.naturalWidth ? `${element.naturalWidth}px` : "100%";

  const imgContent =
    element.naturalWidth && element.naturalHeight ? (
      <div
        className="markdown-image-wrapper"
        style={{
          aspectRatio: `${element.naturalWidth} / ${element.naturalHeight}`,
        }}
      >
        <img
          alt={element.caption || ""}
          src={sanitizeUrl(element.url) || undefined}
          className="markdown-image-aspect"
          draggable={false}
          loading="lazy"
        />
      </div>
    ) : (
      <img
        alt={element.caption || ""}
        src={sanitizeUrl(element.url) || undefined}
        className="markdown-image-auto"
        draggable={false}
        loading="lazy"
      />
    );

  return (
    <div {...attributes} className={`markdown-image-container ${alignClass}`} style={{ maxWidth: displayWidth }}>
      <figure className="relative">
        {element.linkUrl ? (
          <a
            href={sanitizeUrl(element.linkUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="block cursor-pointer"
            aria-label={element.caption || element.alt || "Image link"}
          >
            {imgContent}
          </a>
        ) : (
          imgContent
        )}
        {element.caption && <figcaption className="markdown-image-caption">{element.caption}</figcaption>}
      </figure>
      <span className="hidden">{children}</span>
    </div>
  );
};

const InlineImageComponent = ({ attributes, children, element }: any) => {
  const imgStyle: React.CSSProperties = {
    width: element.width ? `${element.width}px` : "auto",
    height: element.height ? `${element.height}px` : "auto",
    maxHeight: element.width || element.height ? "none" : "3rem",
    display: "inline-block",
    verticalAlign: element.verticalAlign || "middle",
  };

  const inlineImg = (
    <img
      src={sanitizeUrl(element.url)}
      alt={element.alt || ""}
      {...(element.naturalWidth ? { width: element.naturalWidth } : {})}
      {...(element.naturalHeight ? { height: element.naturalHeight } : {})}
      style={imgStyle}
      className={`inline-block ${!element.width && !element.height ? "max-h-12" : ""} w-auto object-contain vertical-align-middle`}
      loading="lazy"
    />
  );

  return (
    <span {...attributes} className="inline-block align-middle mx-1 my-0.5 select-none">
      {element.linkUrl ? (
        <a href={sanitizeUrl(element.linkUrl)} target="_blank" rel="noopener noreferrer" className="inline-block">
          {inlineImg}
        </a>
      ) : (
        inlineImg
      )}
      <span className="hidden">{children}</span>
    </span>
  );
};

function extractYoutubeId(raw: string): string {
  const value = (raw || "").trim();
  try {
    const url = new URL(value);
    if (url.hostname.endsWith("youtu.be")) return url.pathname.slice(1);
    const v = url.searchParams.get("v");
    if (v) return v;
    const m = url.pathname.match(/\/(?:embed|shorts|live)\/([^/?]+)/);
    if (m) return m[1];
  } catch (_) {
    // not a URL: treat as a bare video id
  }
  return value;
}

const YoutubeComponent = ({ attributes, children, element }: any) => {
  const safeVideoId = extractYoutubeId(element.videoId || "").replace(/[^a-zA-Z0-9_-]/g, "");

  return (
    <div {...attributes} className="markdown-video-container">
      <div contentEditable={false} className="w-full h-full">
        {safeVideoId ? (
          <iframe
            src={`https://www.youtube.com/embed/${safeVideoId}`}
            title="YouTube video player"
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">Invalid YouTube video ID</div>
        )}
      </div>
      <span className="hidden">{children}</span>
    </div>
  );
};

// Rendered widget DOM per tweet+theme, reused when the component remounts (avoids re-hitting Twitter).
const tweetDOMCache = new Map<string, HTMLElement>();
// Tweets that failed to load (deleted, private, rate limited). Never retried within the session so a
// remount loop cannot hammer the Twitter syndication endpoint.
const tweetFailedIds = new Set<string>();

const TweetComponent = ({ attributes, children, element }: any) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<"loading" | "loaded" | "failed">("loading");

  useEffect(() => {
    const tweetId = element?.url?.match(/\/status\/(\d+)/)?.[1] ?? null;
    if (!tweetId || tweetFailedIds.has(tweetId)) {
      setStatus("failed");
      return;
    }

    let active = true;
    const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
    const cacheKey = `${tweetId}:${isDark ? "dark" : "light"}`;

    const cached = tweetDOMCache.get(cacheKey);
    if (cached && containerRef.current) {
      containerRef.current.innerHTML = "";
      containerRef.current.appendChild(cached.cloneNode(true));
      setStatus("loaded");
      return;
    }

    setStatus("loading");

    if (!(window as any).twttr) {
      (window as any).twttr = {
        _e: [],
        ready: (f: any) => {
          (window as any).twttr._e.push(f);
        },
      };

      const script = document.createElement("script");
      script.id = "twitter-wjs";
      script.src = "https://platform.twitter.com/widgets.js";
      script.async = true;
      document.head.appendChild(script);
    }

    (window as any).twttr.ready((twttr: any) => {
      if (!active || !containerRef.current) return;
      containerRef.current.innerHTML = "";
      twttr.widgets
        .createTweet(tweetId, containerRef.current, {
          theme: isDark ? "dark" : "light",
          align: "center",
          dnt: true,
          chrome: "transparent",
        })
        .then((el: any) => {
          if (!el) {
            tweetFailedIds.add(tweetId);
            if (active) setStatus("failed");
            return;
          }
          if (containerRef.current) {
            tweetDOMCache.set(cacheKey, containerRef.current.cloneNode(true) as HTMLElement);
          }
          if (active) setStatus("loaded");
        })
        .catch(() => {
          tweetFailedIds.add(tweetId);
          if (active) setStatus("failed");
        });
    });

    return () => {
      active = false;
    };
  }, [element.url]);

  const isLoaded = status === "loaded";

  return (
    <div {...attributes} className="markdown-tweet-container">
      <div
        ref={containerRef}
        contentEditable={false}
        className="w-full flex justify-center"
        style={{
          position: isLoaded ? "relative" : "absolute",
          opacity: isLoaded ? 1 : 0,
          pointerEvents: isLoaded ? "auto" : "none",
        }}
      />
      {status === "loading" && (
        <div
          contentEditable={false}
          className="markdown-tweet-skeleton"
          style={{ minHeight: element.savedHeight ? `${element.savedHeight}px` : "200px" }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 shrink-0 rounded-full bg-muted animate-pulse" />
            <div className="flex flex-col gap-2">
              <div className="h-3 bg-muted rounded-md w-28 animate-pulse" />
              <div className="h-2.5 bg-muted/60 rounded-md w-20 animate-pulse" />
            </div>
            <div className="ml-auto w-5 h-5 rounded-md bg-muted animate-pulse" />
          </div>
          <div className="flex flex-col gap-2.5 mb-4">
            <div className="h-3 bg-muted rounded-md w-full animate-pulse" />
            <div className="h-3 bg-muted rounded-md w-[85%] animate-pulse" />
            <div className="h-3 bg-muted rounded-md w-[60%] animate-pulse" />
          </div>
          <div className="flex-1 w-full rounded-md bg-muted/40 animate-pulse min-h-[40px]" />
        </div>
      )}
      {status === "failed" && (
        <div contentEditable={false} className="markdown-tweet-skeleton flex items-center justify-center text-sm">
          <a href={sanitizeUrl(element.url)} target="_blank" rel="noopener noreferrer" className="markdown-link">
            {element.url}
          </a>
        </div>
      )}
      <span className="hidden">{children}</span>
    </div>
  );
};

const EmbedCardComponent = ({ attributes, children, element }: any) => {
  const safeUrl = sanitizeUrl(element.url);

  return (
    <div {...attributes} className="markdown-embed-card">
      <div contentEditable={false} className="w-full">
        <a href={safeUrl} target="_blank" rel="noreferrer" className="markdown-embed-card-link">
          <div className="markdown-embed-card-body">
            <div className="markdown-embed-card-content">
              <div className="space-y-1">
                {element.title && <h4 className="markdown-embed-card-title">{element.title}</h4>}
                {element.description && <p className="markdown-embed-card-desc">{element.description}</p>}
              </div>

              <div className="markdown-embed-card-domain">
                <LinkIcon className="w-3.5 h-3.5 shrink-0 opacity-70" />
                <span className="truncate">{element.url || safeUrl}</span>
              </div>
            </div>

            {element.image && (
              <div className="markdown-embed-card-image-wrapper">
                <img
                  src={sanitizeUrl(element.image)}
                  alt={element.title || "Link thumbnail"}
                  className="markdown-embed-card-image"
                  onError={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.parentElement) {
                      target.parentElement.style.display = "none";
                    }
                  }}
                />
              </div>
            )}
          </div>
        </a>
      </div>
      <span className="hidden">{children}</span>
    </div>
  );
};

const LinkComponent = ({ attributes, children, element }: any) => {
  const safeUrl = sanitizeUrl(element.url);
  return (
    <a {...attributes} className="markdown-link" target="_blank" href={safeUrl} rel="noopener noreferrer">
      {children}
    </a>
  );
};

// ─── Element Switch ──────────────────────────────────────────────────────────
export const Element = ({ attributes, children, element }: any) => {
  const style = { textAlign: element.align };

  switch (element.type) {
    case "heading-one":
      return (
        <HeadingComponent tag="h1" className="markdown-h1" attributes={attributes} children={children} element={element} style={style} />
      );
    case "heading-two":
      return (
        <HeadingComponent tag="h2" className="markdown-h2" attributes={attributes} children={children} element={element} style={style} />
      );
    case "heading-three":
      return (
        <HeadingComponent tag="h3" className="markdown-h3" attributes={attributes} children={children} element={element} style={style} />
      );
    case "heading-four":
      return (
        <HeadingComponent tag="h4" className="markdown-h4" attributes={attributes} children={children} element={element} style={style} />
      );
    case "heading-five":
      return (
        <HeadingComponent tag="h5" className="markdown-h5" attributes={attributes} children={children} element={element} style={style} />
      );
    case "heading-six":
      return (
        <HeadingComponent tag="h6" className="markdown-h6" attributes={attributes} children={children} element={element} style={style} />
      );

    case "bulleted-list-item":
      return <BulletedListItemComponent attributes={attributes} children={children} />;
    case "numbered-list-item":
      return <NumberedListItemComponent attributes={attributes} children={children} element={element} />;
    case "check-list-item":
      return <CheckListItemComponent attributes={attributes} children={children} element={element} />;

    case "block-quote":
      return <BlockQuoteComponent attributes={attributes} children={children} style={style} />;
    case "code-block":
      return <CodeBlockComponent attributes={attributes} children={children} element={element} />;
    case "code-tabs":
      return <CodeTabsComponent attributes={attributes} children={children} element={element} />;
    case "doc-card-grid":
      return (
        <div {...attributes} className="markdown-doc-card-grid not-prose">
          {children}
        </div>
      );
    case "horizontal-rule":
      return <HorizontalRuleComponent attributes={attributes} children={children} />;

    case "bulleted-list":
      return (
        <div className="markdown-legacy-list space-y-1.5" style={style} {...attributes}>
          <ListContext.Provider value="bullet">{children}</ListContext.Provider>
        </div>
      );
    case "numbered-list":
      return (
        <div className="markdown-legacy-list space-y-1.5 count-list" style={style} {...attributes}>
          <ListContext.Provider value="number">{children}</ListContext.Provider>
        </div>
      );
    case "list-item":
      return <ListItemComponent attributes={attributes} children={children} style={style} />;

    case "table":
      return <TableComponent attributes={attributes} children={children} element={element} />;
    case "table-row":
      return (
        <tr className="divide-x divide-border" {...attributes}>
          {children}
        </tr>
      );
    case "table-cell":
      return <TableCellComponent attributes={attributes} children={children} element={element} />;

    case "toggle-list":
      return <ToggleListComponent attributes={attributes} children={children} element={element} />;
    case "toggle-title":
      return (
        <div
          className={`markdown-toggle-title ${
            isNodeContentEmpty(element)
              ? "before:content-['Toggle_details'] before:absolute before:left-0 before:text-muted-foreground/70 before:pointer-events-none before:select-none before:font-normal before:opacity-50"
              : ""
          }`}
          {...attributes}
        >
          {children}
        </div>
      );
    case "toggle-content":
      return (
        <div className="markdown-toggle-content" {...attributes}>
          {isNodeContentEmpty(element) && (
            <span
              contentEditable={false}
              className="absolute left-0 top-1.5 text-sm text-muted-foreground/50 pointer-events-none select-none"
            >
              Empty toggle.
            </span>
          )}
          {children}
        </div>
      );

    case "tweet":
      return <TweetComponent attributes={attributes} children={children} element={element} />;
    case "youtube":
      return <YoutubeComponent attributes={attributes} children={children} element={element} />;
    case "embed-card":
      return <EmbedCardComponent attributes={attributes} children={children} element={element} />;

    case "link":
      return <LinkComponent attributes={attributes} children={children} element={element} />;
    case "inline-image":
      return <InlineImageComponent attributes={attributes} children={children} element={element} />;
    case "image":
      return <ImageComponent attributes={attributes} children={children} element={element} />;

    default:
      return <ParagraphComponent attributes={attributes} children={children} element={element} style={style} />;
  }
};

// ─── Leaf Formatter ─────────────────────────────────────────────────────────
export const Leaf = ({ attributes, children, leaf }: any) => {
  if (leaf.bold) {
    children = <span className="font-semibold">{children}</span>;
  }

  if (leaf.code) {
    children = <code className="markdown-inline-code">{children}</code>;
  }

  if (leaf.italic) {
    children = <span className="italic">{children}</span>;
  }

  if (leaf.underline) {
    children = <span className="underline">{children}</span>;
  }

  if (leaf.strikethrough) {
    children = <span className="line-through">{children}</span>;
  }

  if (leaf.textColor) {
    const className = TEXT_COLOR_MAP[leaf.textColor] || "";
    const style = className ? {} : { color: leaf.textColor };
    children = (
      <span className={className} style={style}>
        {children}
      </span>
    );
  }

  if (leaf.highlight) {
    const className = HIGHLIGHT_MAP[leaf.highlight] || "";
    const style = className ? {} : { backgroundColor: leaf.highlight };
    children = (
      <span className={className} style={style}>
        {children}
      </span>
    );
  }

  if (leaf.token && leaf.tokenType) {
    children = <span className={`token ${leaf.tokenType}`}>{children}</span>;
  }

  return <span {...attributes}>{children}</span>;
};

// ─── Main MarkdownRenderer Component ─────────────────────────────────────────
export interface MarkdownRendererProps {
  content: string;
  className?: string;
  enableDocsFeatures?: boolean;
}

export function MarkdownRenderer({ content, className = "", enableDocsFeatures = true }: MarkdownRendererProps) {
  const renderElement = useCallback((props: any) => <Element {...props} />, []);
  const renderLeaf = useCallback((props: any) => <Leaf {...props} />, []);

  const editor = useMemo(() => {
    const e = withReact(createEditor());
    const { isVoid, isInline } = e;
    e.isVoid = (element: any) => {
      return ["image", "inline-image", "horizontal-rule", "tweet", "youtube", "embed-card"].includes(element.type) ? true : isVoid(element);
    };
    e.isInline = (element: any) => {
      return ["link", "inline-image"].includes(element.type) ? true : isInline(element);
    };
    return e;
  }, []);

  // The first render uses `content` immediately (SSR/article pages). Later changes (live preview while
  // typing) are debounced because every change remounts the tree, which reloads tweets/videos.
  const [renderedContent, setRenderedContent] = useState(content);
  useEffect(() => {
    if (content === renderedContent) return;
    const timer = setTimeout(() => setRenderedContent(content), 250);
    return () => clearTimeout(timer);
  }, [content, renderedContent]);

  const nodes = useMemo(() => {
    return toSlateNodes(renderedContent, enableDocsFeatures);
  }, [renderedContent, enableDocsFeatures]);

  return (
    <DocsFeaturesContext.Provider value={enableDocsFeatures}>
      <Slate key={renderedContent} editor={editor} initialValue={nodes}>
        <Editable
          readOnly={true}
          placeholder=""
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          className={`markdown-renderer ${className}`}
        />
      </Slate>
    </DocsFeaturesContext.Provider>
  );
}

export default MarkdownRenderer;
